import './style.scss';
import './tabs.scss';

import { useEffect, useState } from 'react';

import { getUserData, type ResultType } from '../../../constants/global';
import { useGlobalContext } from '../../../context/global';
import { type IAgentAssignmentItem } from '../../../interface/agent-assignment';
import { type ICitizensSitrepContext } from '../../../interface/child-agent';
import { type IAgentChatPanelData } from '../../../interface/peer-chat';
import { type IResultAnalyticsData } from '../../../interface/state/IElectionState';
import { getAssignmentsByEntity } from '../../../lib/agent-assignment-api';
import { childEntityLabel, childEntityLabelSingular, childScopeOf, type IChildScope } from '../overview/quick-analytics/utils';
import { ReconciliationDrawerStrip, reconTargetFromChildScope } from '../reconciliation/ReconciliationDrawerStrip';
import AgentSummaryStrip from './AgentSummaryStrip';
import ChildAgentTabs, { type ChildTab, type IChildAgentTab, type TabHint } from './ChildAgentTabs';
import { AgentErrorState, AgentSkeleton, CoverageGapState, EntityContextHeader } from './components';
import AgentTab from './tabs/AgentTab';
import ChildrenTab from './tabs/ChildrenTab';
import MapTab from './tabs/MapTab';
import ResultTab from './tabs/ResultTab';
import SitrepTab from './tabs/SitrepTab';

type LoadState = 'loading' | 'ready' | 'error' | 'forbidden';

export interface IChildAgentData {
	entityCode: string;
	entityName?: string;
	level: string;
	electionId?: number;
	analyticsStatus?: { isUploaded?: boolean; hasConflict?: boolean; dateUploaded?: string };
	// Optional hierarchy context to unlock citizens-agent situation reports (source b).
	sitrepCtx?: Partial<ICitizensSitrepContext>;
	// Scope for the "children" tab (this entity's own children). Null/absent for a
	// polling unit (leaf) — the tab is then hidden.
	childScope?: IChildScope | null;
	// Which tab to land on once loaded; the conversation's result chip asks for 'result'.
	initialTab?: ChildTab;
	// True when this entity is one rung below the reader's own (a DIRECT child). Only a direct
	// child can be messaged in-app; a drilled grandchild gets no Message button.
	isDirectChild?: boolean;
}

// Translate the result-analytics row's own status into a small context chip for
// the coverage-gap fallback (when no agent is assigned).
const analyticsChipOf = (s?: IChildAgentData['analyticsStatus']) => {
	if (!s) return null;
	if (s.hasConflict) return { label: 'Conflict', tone: 'danger' as const };
	const realDate = !!s.dateUploaded && !s.dateUploaded.startsWith('0001');
	if (s.isUploaded && realDate) return { label: 'Uploaded', tone: 'success' as const };
	return { label: 'Pending', tone: 'warning' as const };
};

// The Result tab's at-a-glance dot, derived from the assignment meta.
const resultHintOf = (item: IAgentAssignmentItem): TabHint => {
	const meta = item.assignmentMeta;
	if (meta?.hasConflict) return { tone: 'danger' };
	if (meta?.isUploaded && meta?.resultId) return { tone: 'success' };
	return { tone: 'warning' };
};

// Compact tab label for the children tab (the tab-bar's narrow-viewport form).
const childTabShort = (resultType: string): string => {
	switch (resultType.toLowerCase()) {
		case 'ec8a':
			return 'Units';
		case 'ec8b':
			return 'Wards';
		case 'ec8c':
			return 'LGAs';
		case 'ec8d':
			return 'States';
		default:
			return 'Children';
	}
};

const ChildAgent = () => {
	const { rsProps, peerInbox } = useGlobalContext();
	const data = (rsProps?.data ?? {}) as IChildAgentData;
	const { entityCode, level, electionId } = data;
	const me = Number(getUserData().user?.UserId ?? 0);
	const childScope = data.childScope ?? null;
	const hasChildren = !!childScope;

	const [state, setState] = useState<LoadState>('loading');
	const [item, setItem] = useState<IAgentAssignmentItem | null>(null);
	const [tab, setTab] = useState<ChildTab>('agent');
	const [sitrepCount, setSitrepCount] = useState<number | null>(null);
	const [mapHint, setMapHint] = useState<TabHint | undefined>(undefined);
	const [childCount, setChildCount] = useState<number | null>(null);

	const load = () => {
		if (!entityCode || !electionId) {
			setState('error');
			return;
		}
		setState('loading');
		getAssignmentsByEntity([entityCode], electionId).then((outcome) => {
			if (!outcome.reachable) {
				setState('error');
				return;
			}
			if (outcome.httpStatus === 403) {
				setState('forbidden');
				return;
			}
			if (outcome.httpStatus < 200 || outcome.httpStatus >= 300) {
				setState('error');
				return;
			}
			// Left-join guarantees one item per requested code; default to a coverage gap.
			const loaded = outcome.body?.data?.items?.[0] ?? null;
			setItem(loaded);
			// Land on the children view for an unassigned parent (what the user tapped
			// to see), otherwise the agent view.
			setTab(!loaded?.isAssigned && hasChildren ? 'children' : (data.initialTab ?? 'agent'));
			setState('ready');
		});
	};

	// Reset transient tab state whenever the drawer targets a new entity.
	useEffect(() => {
		setTab('agent');
		setSitrepCount(null);
		setMapHint(undefined);
		setChildCount(null);
		load();
	}, [entityCode, electionId]);

	// Open a child's own Agent Coverage drawer, stacked on top (Back returns here).
	const openChildDrawer = (row: IResultAnalyticsData) => {
		rsProps?.addRightSectionHistory();
		rsProps?.callSection({
			action: 'view',
			component: 'child-agent',
			title: 'Agent Coverage',
			data: {
				entityCode: row.code,
				entityName: row.code,
				level: childEntityLabelSingular(row.resultType.toUpperCase()),
				electionId,
				analyticsStatus: {
					isUploaded: row.isUploaded,
					hasConflict: row.hasConflict,
					dateUploaded: row.dateUploaded,
				},
				childScope: childScopeOf(row),
			},
		});
	};

	if (state === 'loading') return <AgentSkeleton />;
	if (state === 'forbidden')
		return (
			<AgentErrorState
				onRetry={load}
				forbidden
			/>
		);
	if (state === 'error') return <AgentErrorState onRetry={load} />;

	const isAssigned = !!item?.isAssigned;

	// Leaf entity with no assigned agent: nothing to surface at all → the coverage-gap
	// state with no tabs (unchanged behavior for e.g. an uncovered polling unit).
	if (!isAssigned && !hasChildren) {
		return (
			<div className='child-agent f-column-20'>
				<EntityContextHeader
					level={level}
					code={entityCode}
					analyticsChip={analyticsChipOf(data.analyticsStatus)}
				/>
				<CoverageGapState level={level} />
			</div>
		);
	}

	const resultId = item?.assignmentMeta?.resultId ?? null;

	// Message this agent in the app. Only for a direct child, never for the reader themself.
	const chatAgent = item?.agent;
	const canMessage = !!data.isDirectChild && !!chatAgent && chatAgent.id !== me && !!electionId;
	const openChat = () => {
		if (!chatAgent || !electionId) return;
		const payload: IAgentChatPanelData = {
			counterpart: {
				coreUserId: chatAgent.id,
				name: `${chatAgent.firstName ?? ''} ${chatAgent.lastName ?? ''}`.trim() || chatAgent.userName || 'Agent',
				entityCode,
				level,
				role: 'child',
				phone: chatAgent.phoneNumber?.trim() || undefined,
				email: chatAgent.email?.trim() || undefined,
			},
			electionId,
			origin: 'coverage',
			coverageData: data as unknown as Record<string, unknown>,
		};
		// Pushed onto the panel history so Back returns here — on the Agent tab, where the
		// button lives, because this drawer remounts and resets its tab on the way back.
		rsProps?.addRightSectionHistory();
		rsProps?.callSection({ action: 'view', component: 'agent-chat', title: payload.counterpart.name, data: payload });
	};
	const unreadFromAgent = chatAgent ? (peerInbox?.unreadByCounterpart.get(chatAgent.id) ?? 0) : 0;

	// Build the tab set: the agent view is always present (identity when assigned, the
	// coverage-gap notice when not); result/reports/map only when an agent exists; the
	// children tab whenever this entity has a level below it.
	const tabs: IChildAgentTab[] = [
		{ key: 'agent', label: isAssigned ? 'Assigned Agent' : 'Coverage', short: isAssigned ? 'Agent' : 'Coverage' },
	];
	if (isAssigned && item) {
		tabs.push(
			{ key: 'result', label: 'Result', short: 'Result', hint: resultHintOf(item) },
			{
				key: 'sitrep',
				label: 'Situation Reports',
				short: 'Reports',
				hint: sitrepCount != null ? { tone: 'neutral', count: sitrepCount } : undefined,
			},
			{ key: 'map', label: 'Live Map', short: 'Map', hint: mapHint }
		);
	}
	if (hasChildren && childScope) {
		tabs.push({
			key: 'children',
			label: childEntityLabel(childScope.resultType),
			short: childTabShort(childScope.resultType),
			hint: childCount != null ? { tone: 'neutral', count: childCount } : undefined,
		});
	}

	// Guard against an active tab that isn't in the current set (e.g. the optimistic
	// 'agent' default before load resolves to an unassigned-parent-only children view).
	const activeTab: ChildTab = tabs.some((t) => t.key === tab) ? tab : tabs[0].key;

	return (
		<div className='child-agent f-column-20'>
			{isAssigned && item ? (
				<AgentSummaryStrip
					item={item}
					level={level}
					code={entityCode}
					unread={canMessage ? unreadFromAgent : 0}
				/>
			) : (
				<EntityContextHeader
					level={level}
					code={entityCode}
					analyticsChip={analyticsChipOf(data.analyticsStatus)}
				/>
			)}
			{hasChildren ? <ReconciliationDrawerStrip target={reconTargetFromChildScope(childScope, entityCode, electionId)} /> : null}
			<ChildAgentTabs
				tabs={tabs}
				active={activeTab}
				onChange={setTab}
			/>
			<div
				className='child-agent__panel'
				role='tabpanel'
				id={`ca-panel-${activeTab}`}
				aria-labelledby={`ca-tab-${activeTab}`}
			>
				{activeTab === 'agent' ? (
					isAssigned && item ? (
						<AgentTab
							item={item}
							seed={entityCode}
							message={canMessage ? { onClick: openChat, unread: unreadFromAgent } : undefined}
						/>
					) : (
						<CoverageGapState level={level} />
					)
				) : null}
				{activeTab === 'result' && item ? (
					<ResultTab
						level={item.level}
						resultId={resultId}
						coverage={item.assignmentMeta?.hasConflict ? 'conflict' : 'pending'}
						agent={item.agent}
					/>
				) : null}
				{activeTab === 'sitrep' && item ? (
					<SitrepTab
						agent={item.agent}
						sitrepCtx={data.sitrepCtx}
						onCount={setSitrepCount}
					/>
				) : null}
				{activeTab === 'map' && item ? (
					<MapTab
						entityCode={entityCode}
						agent={item.agent}
						onHint={setMapHint}
					/>
				) : null}
				{activeTab === 'children' && childScope ? (
					<ChildrenTab
						childScope={childScope}
						electionId={electionId}
						onOpenChild={openChildDrawer}
						onCount={setChildCount}
						rootLabel={entityCode}
						rootLevelLabel={level}
					/>
				) : null}
			</div>
		</div>
	);
};

export default ChildAgent;
